import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { EducationalResourceModalPresenter } from "./EducationalResourceModal.presenter";
import {
  EducationalResource,
  EducationalResourceCreateRequestPayload,
  EducationalResourceUpdateRequestPayload,
} from "@/domain/educational-resource.interface";
import {
  useEducationalResourceCreateOne,
  useEducationalResourceUpdateOne,
} from "@/hooks/useEducationalResources";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import validationSchema, {
  EducationalResourceFormData,
} from "./validation.schema";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal";
import { ErrorResponse } from "@/domain/error.interface";
import { EducationalResourceStatusEnum } from "@/domain/educational-resource.enum";
import { useCategoryByType } from "@/hooks/useCategories";

export interface EducationalResourceModalContainerProps {
  open: boolean;
  mode: "create" | "edit";
  educationalResource?: EducationalResource | null;
  onClose: () => void;
  onSuccess?: () => void;
  canAddEducationalResource: boolean;
  canEditEducationalResource: boolean;
}

export const EducationalResourceModalContainer: FC<
  EducationalResourceModalContainerProps
> = ({
  open,
  mode,
  educationalResource,
  onClose,
  onSuccess,
  canAddEducationalResource,
  canEditEducationalResource,
}) => {
  const { t } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();

  // Mutations
  const educationalResourceCreateOne = useEducationalResourceCreateOne();
  const educationalResourceUpdateOne = useEducationalResourceUpdateOne();

  // Fetch categories
  const { category, isLoadingCategory } = useCategoryByType({
    type: "EDUCATIONAL_RESOURCE",
    status: "ACTIVE",
  });

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPermissionDeniedModalOpen, setIsPermissionDeniedModalOpen] =
    useState(false);

  // Form setup
  const methods = useForm<EducationalResourceFormData>({
    resolver: yupResolver(validationSchema(t)),
    mode: "all",
    defaultValues: {
      categoryId: "",
      type: undefined,
      status: EducationalResourceStatusEnum.ACTIVE,
      languages: [
        {
          language: "en-us",
          title: "",
          description: "",
          url: "",
        },
      ],
      attributes: {
        thumbnailUrl: "",
        duration: null,
      },
      priority: 0,
    },
  });

  // Initialize form data when educationalResource is loaded (edit mode)
  useEffect(() => {
    if (mode === "edit" && educationalResource && open) {
      methods.reset({
        categoryId: educationalResource.categoryId,
        type: educationalResource.type,
        status: educationalResource.status,
        languages: educationalResource.languages,
        attributes: educationalResource.attributes || {
          thumbnailUrl: "",
          duration: null,
        },
        priority: educationalResource.priority,
      });
    } else if (mode === "create" && open) {
      methods.reset({
        categoryId: "",
        type: undefined,
        status: EducationalResourceStatusEnum.ACTIVE,
        languages: [
          {
            language: "en-us",
            title: "",
            description: "",
            url: "",
          },
        ],
        attributes: {
          thumbnailUrl: "",
          duration: null,
        },
        priority: 0,
      });
    }
  }, [mode, educationalResource, open, methods]);

  // Handle submit
  const handleSubmit = async () => {
    if (mode === "create" && !canAddEducationalResource) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }
    if (mode === "edit" && !canEditEducationalResource) {
      showSnackBar(t("generic_not_allowed") || "Not allowed", "error");
      return;
    }

    const isValid = await methods.trigger();
    if (!isValid) {
      return;
    }

    const formDataValues = methods.getValues();

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const payload: EducationalResourceCreateRequestPayload = {
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          categoryId: formDataValues.categoryId,
          type: formDataValues.type,
          status: formDataValues.status,
          languages: formDataValues.languages,
          attributes: formDataValues.attributes,
          priority: formDataValues.priority,
          createdBy: userInfo?._id || "",
        } as EducationalResourceCreateRequestPayload;

        await educationalResourceCreateOne.mutateAsync(payload);
        showSnackBar("Educational Resource created successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.CREATE,
          actionCode: ACTION_CODE.EDUCATIONAL_RESOURCES_CREATE,
          action: "When creating a new educational resource",
          detail: `Created educational resource with category ${formDataValues.categoryId}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      } else {
        const payload: EducationalResourceUpdateRequestPayload = {
          _id: educationalResource?._id || "",
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          categoryId: formDataValues.categoryId,
          type: formDataValues.type,
          status: formDataValues.status,
          languages: formDataValues.languages,
          attributes: formDataValues.attributes,
          priority: formDataValues.priority,
          updatedBy: userInfo?._id || "",
        } as EducationalResourceUpdateRequestPayload;

        await educationalResourceUpdateOne.mutateAsync({
          id: educationalResource?._id || "",
          data: payload,
        });
        showSnackBar("Educational Resource updated successfully", "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.EDIT,
          actionCode: ACTION_CODE.EDUCATIONAL_RESOURCES_EDIT,
          action: "When editing an educational resource",
          detail: `Edited educational resource ${educationalResource?._id}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as ErrorResponse;
      const errorMessage = Array.isArray(err.message)
        ? err.message[0]
        : err.message || err.error || "An error occurred";

      showSnackBar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <EducationalResourceModalPresenter
          open={open}
          mode={mode}
          isSubmitting={
            isSubmitting ||
            educationalResourceCreateOne.isPending ||
            educationalResourceUpdateOne.isPending
          }
          isLoadingData={false}
          onClose={onClose}
          onSubmit={handleSubmit}
          categories={category || []}
          isLoadingCategories={isLoadingCategory}
        />
      </FormProvider>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={isPermissionDeniedModalOpen}
        onClose={() => setIsPermissionDeniedModalOpen(false)}
      />
    </>
  );
};
